import { useMemo, useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PlotCard } from "../PlotCard";

type Metric = "totalOpen" | "avgOpen" | "totalVolume" | "perMetre";

interface Props {
  fromIso: string;
  toIso: string;
}

const METRIC_LABEL: Record<Metric, { y: string; title: string }> = {
  totalOpen:    { y: "Time (s)",    title: "Total time open" },
  avgOpen:      { y: "Time (s)",    title: "Avg time per visit" },
  totalVolume:  { y: "Volume (L)",  title: "Total volume" },
  perMetre:     { y: "s / m",       title: "Time per metre" },
};

/**
 * One bar per box, plus three bars per box for valve 1 / 2 / 3.
 * Mirrors `Time_Open_Bar_Graph.png`, `Avg_Time_per_Visit_Bar_Graph.png`,
 * and `Total_Time_Normalized_by_Chainage.png`.
 */
export function PerBoxBarChart({ fromIso, toIso }: Props) {
  const [metric, setMetric] = useState<Metric>("totalOpen");

  const series = useQuery({
    queryKey: ["lubrication", "series", fromIso, toIso],
    queryFn: () => api.lubrication.series({ from: fromIso, to: toIso }),
    refetchInterval: 60_000,
  });

  const data = useMemo(() => {
    const records = series.data?.records ?? [];

    // Bucket per (box, valve). Track sum-time, sum-volume, visit count,
    // and the chainage span (max - min) for the per-metre metric.
    type Cell = { time: number; vol: number; visits: number; chMin: number; chMax: number };
    const empty = (): Cell => ({
      time: 0, vol: 0, visits: 0,
      chMin: Number.POSITIVE_INFINITY, chMax: Number.NEGATIVE_INFINITY,
    });
    const byBox = new Map<number, { all: Cell; v1: Cell; v2: Cell; v3: Cell }>();

    // Visit counting: a "visit" is a contiguous run of the same
    // (box, valve). Walk in order.
    let lastBox: number | null = null;
    let lastValve: number | null = null;

    for (const r of records) {
      if (r.boxNo == null) continue;
      let cell = byBox.get(r.boxNo);
      if (!cell) {
        cell = { all: empty(), v1: empty(), v2: empty(), v3: empty() };
        byBox.set(r.boxNo, cell);
      }
      const valveCell =
        r.valveNo === 1 ? cell.v1 :
        r.valveNo === 2 ? cell.v2 :
        r.valveNo === 3 ? cell.v3 :
        null;
      const targets = [cell.all, valveCell].filter(Boolean) as Cell[];
      for (const t of targets) {
        if (r.valveOpenS != null) t.time += r.valveOpenS;
        if (r.igvL != null) t.vol += r.igvL;
        if (r.chainageM != null) {
          if (r.chainageM < t.chMin) t.chMin = r.chainageM;
          if (r.chainageM > t.chMax) t.chMax = r.chainageM;
        }
      }
      const isNewVisit = r.boxNo !== lastBox || r.valveNo !== lastValve;
      if (isNewVisit) {
        cell.all.visits++;
        if (valveCell) valveCell.visits++;
      }
      lastBox = r.boxNo;
      lastValve = r.valveNo ?? null;
    }

    function valueOf(c: Cell): number | null {
      switch (metric) {
        case "totalOpen":   return c.time;
        case "avgOpen":     return c.visits > 0 ? c.time / c.visits : 0;
        case "totalVolume": return c.vol;
        case "perMetre": {
          const span = c.chMax - c.chMin;
          if (!Number.isFinite(span) || span <= 0) return null;
          return c.time / span;
        }
      }
    }

    const boxes = [...byBox.keys()].sort((a, b) => a - b);
    const x = boxes.map((b) => `${b}`);

    return [
      {
        x, y: boxes.map((b) => valueOf(byBox.get(b)!.all)),
        type: "bar", name: "Overall",
        marker: { color: "#000" },
      },
      {
        x, y: boxes.map((b) => valueOf(byBox.get(b)!.v1)),
        type: "bar", name: "Port 1", marker: { color: "#ea580c" },
      },
      {
        x, y: boxes.map((b) => valueOf(byBox.get(b)!.v2)),
        type: "bar", name: "Port 2", marker: { color: "#16a34a" },
      },
      {
        x, y: boxes.map((b) => valueOf(byBox.get(b)!.v3)),
        type: "bar", name: "Port 3", marker: { color: "#7e22ce" },
      },
    ] as Record<string, unknown>[];
  }, [series.data, metric]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10.5px] tracking-wider uppercase text-dim">Metric</span>
        <Segmented
          value={metric}
          onChange={setMetric}
          options={[
            { id: "totalOpen",   label: "Total open" },
            { id: "avgOpen",     label: "Avg / visit" },
            { id: "totalVolume", label: "Total vol" },
            { id: "perMetre",    label: "s / m" },
          ]}
        />
      </div>
      <PlotCard
        title={METRIC_LABEL[metric].title}
        subtitle="Per box; overall + per port"
        data={data as never}
        height={400}
        uirevision={`box-${metric}-${fromIso}`}
        layout={{
          margin: { l: 56, r: 16, t: 16, b: 40 },
          barmode: "group",
          xaxis: { title: { text: "Box nr" }, type: "category" },
          yaxis: { title: { text: METRIC_LABEL[metric].y }, rangemode: "tozero" },
          legend: {
            x: 0.99, y: 0.99, xanchor: "right", yanchor: "top",
            bgcolor: "rgba(255,255,255,0.85)",
            bordercolor: "rgba(0,0,0,0.08)", borderwidth: 1,
            font: { size: 10, color: "#374151" },
          },
          showlegend: true,
        }}
      />
    </div>
  );
}

function Segmented<T extends string>({
  value, onChange, options,
}: { value: T; onChange: (v: T) => void; options: { id: T; label: string }[] }) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            value === o.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
